import config from "appConfig";

const theory = `<p>
To illustrate the concept of eigenmode, we consider here the case of a medium bounded by two perfectly reflecting rigid walls (<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <mi>v</mi>
   <mo class=MathClass-rel>=</mo>
   <mn>0</mn>
   <mo class=MathClass-rel>⇔</mo>
   <msub>
      <mrow>
         <mi>R</mi>
      </mrow>
      <mrow>
         <mi>p</mi>
      </mrow>
   </msub>
   <mo class=MathClass-rel>=</mo>
   <mn>1</mn>
</math>) located respectively at
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <mi>x</mi>
   <mo class=MathClass-rel>=</mo>
   <mn>0</mn>
</math>
and
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <mi>x</mi>
   <mo class=MathClass-rel>=</mo>
   <mi>L</mi>
</math> (see <a href="#" data-img-src=${config.ASSETS_URL}/01/08/theory/figure-1.svg data-img-alt="Figure 1. Medium bounded by two perfectly reflecting rigid walls.">Figure 1</a>).
<figure class=figure>
<p><img alt="Figure 1. Medium bounded by two perfectly reflecting rigid walls." src=${config.ASSETS_URL}/01/08/theory/figure-1.svg><figcaption class=caption>Figure 1. Medium bounded by two perfectly reflecting rigid walls.</figcaption></figure>
<h3 class=sectionHead>Solutions in time domain</h3>
<p>The general solution of the wave equation in time domain can be written for the pressure and velocity
<table class=equation>
<tr>
   <td>
      <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
         <mi>p</mi>
         <mo class=MathClass-open>(</mo>
         <mi>x</mi>
         <mo class=MathClass-punc>,</mo>
         <mi>t</mi>
         <mo class=MathClass-close>)</mo>
         <mo class=MathClass-rel>=</mo>
         <mi>f</mi>
         <mo class=MathClass-open>(</mo>
         <mi>x</mi>
         <mo class=MathClass-bin>−</mo>
         <mi mathvariant=italic>ct</mi>
         <mo class=MathClass-close>)</mo>
         <mo class=MathClass-bin>+</mo>
         <mi>g</mi>
         <mo class=MathClass-open>(</mo>
         <mi>x</mi>
         <mo class=MathClass-bin>+</mo>
         <mi mathvariant=italic>ct</mi>
         <mo class=MathClass-close>)</mo>
      </math>
   <td class=eq-no>(1)
<tr>
   <td>
      <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
         <mi>v</mi>
         <mo class=MathClass-open>(</mo>
         <mi>x</mi>
         <mo class=MathClass-punc>,</mo>
         <mi>t</mi>
         <mo class=MathClass-close>)</mo>
         <mo class=MathClass-rel>=</mo>
         <mfrac>
            <mrow>
               <mi>f</mi>
               <mo class=MathClass-open>(</mo>
               <mi>x</mi>
               <mo class=MathClass-bin>−</mo>
               <mi mathvariant=italic>ct</mi>
               <mo class=MathClass-close>)</mo>
               <mo class=MathClass-bin>−</mo>
               <mi>g</mi>
               <mo class=MathClass-open>(</mo>
               <mi>x</mi>
               <mo class=MathClass-bin>+</mo>
               <mi mathvariant=italic>ct</mi>
               <mo class=MathClass-close>)</mo>
            </mrow>
            <mrow>
               <msub>
                  <mrow>
                     <mi>ρ</mi>
                  </mrow>
                  <mrow>
                     <mn>0</mn>
                  </mrow>
               </msub>
               <mi>c</mi>
            </mrow>
         </mfrac>
      </math>
   <td class=eq-no>(2)
</table>
<p>
At
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <mi>x</mi>
   <mo class=MathClass-rel>=</mo>
   <mn>0</mn>
</math>
for a rigid wall
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <mi>v</mi>
   <mo class=MathClass-open>(</mo>
   <mn>0</mn>
   <mo class=MathClass-punc>,</mo>
   <mi>t</mi>
   <mo class=MathClass-close>)</mo>
   <mo class=MathClass-rel>=</mo>
   <mn>0</mn>
</math>
then
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <mi>g</mi>
   <mo class=MathClass-open>(</mo>
   <mi mathvariant=italic>ct</mi>
   <mo class=MathClass-close>)</mo>
   <mo class=MathClass-rel>=</mo>
   <mi>f</mi>
   <mo class=MathClass-open>(</mo>
   <mo class=MathClass-bin>−</mo>
   <mi mathvariant=italic>ct</mi>
   <mo class=MathClass-close>)</mo>
</math>. As this is true for any value of the argument
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <mi mathvariant=italic>ct</mi>
</math>
this leads to
<table class=equation>
<tr>
   <td>
      <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
         <mi>v</mi>
         <mo class=MathClass-open>(</mo>
         <mi>x</mi>
         <mo class=MathClass-punc>,</mo>
         <mi>t</mi>
         <mo class=MathClass-close>)</mo>
         <mo class=MathClass-rel>=</mo>
         <mfrac>
            <mrow>
               <mi>f</mi>
               <mo class=MathClass-open>(</mo>
               <mi>x</mi>
               <mo class=MathClass-bin>−</mo>
               <mi mathvariant=italic>ct</mi>
               <mo class=MathClass-close>)</mo>
               <mo class=MathClass-bin>−</mo>
               <mi>f</mi>
               <mo class=MathClass-open>(</mo>
               <mo class=MathClass-bin>−</mo>
               <mi>x</mi>
               <mo class=MathClass-bin>−</mo>
               <mi mathvariant=italic>ct</mi>
               <mo class=MathClass-close>)</mo>
            </mrow>
            <mrow>
               <msub>
                  <mrow>
                     <mi>ρ</mi>
                  </mrow>
                  <mrow>
                     <mn>0</mn>
                  </mrow>
               </msub>
               <mi>c</mi>
            </mrow>
         </mfrac>
      </math>
   <td class=eq-no>(3)
</table>
<p>
At
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <mi>x</mi>
   <mo class=MathClass-rel>=</mo>
   <mi>L</mi>
</math>,
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <mi>v</mi>
   <mo class=MathClass-open>(</mo>
   <mi>L</mi>
   <mo class=MathClass-punc>,</mo>
   <mi>t</mi>
   <mo class=MathClass-close>)</mo>
   <mo class=MathClass-rel>=</mo>
   <mn>0</mn>
</math>
then
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <mi>f</mi>
   <mo class=MathClass-open>(</mo>
   <mi>L</mi>
   <mo class=MathClass-bin>−</mo>
   <mi mathvariant=italic>ct</mi>
   <mo class=MathClass-close>)</mo>
   <mo class=MathClass-rel>=</mo>
   <mi>f</mi>
   <mo class=MathClass-open>(</mo>
   <mo class=MathClass-bin>−</mo>
   <mi>L</mi>
   <mo class=MathClass-bin>−</mo>
   <mi mathvariant=italic>ct</mi>
   <mo class=MathClass-close>)</mo>
</math>. Setting
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <mi>X</mi>
   <mo class=MathClass-rel>=</mo>
   <mi>L</mi>
   <mo class=MathClass-bin>−</mo>
   <mi mathvariant=italic>ct</mi>
</math>, one has
<table class=equation>
<tr>
   <td>
      <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
         <mi>f</mi>
         <mo class=MathClass-open>(</mo>
         <mi>X</mi>
         <mo class=MathClass-close>)</mo>
         <mo class=MathClass-rel>=</mo>
         <mi>f</mi>
         <mo class=MathClass-open>(</mo>
         <mi>X</mi>
         <mo class=MathClass-bin>−</mo>
         <mn>2</mn>
         <mi>L</mi>
         <mo class=MathClass-close>)</mo>
      </math>
   <td class=eq-no>(4)
</table>
<p>
Function
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <mi>f</mi>
</math>
is periodic with a spatial period
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <mn>2</mn>
   <mi>L</mi>
</math>
which can be associated to the time period
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <mi>T</mi>
   <mo class=MathClass-rel>=</mo>
   <mn>2</mn>
   <mi>L</mi>
   <mo class=MathClass-bin>∕</mo>
   <mi>c</mi>
</math>
corresponding to the eigenfrequency
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <msub>
      <mrow>
         <mi>f</mi>
      </mrow>
      <mrow>
         <mn>1</mn>
      </mrow>
   </msub>
   <mo class=MathClass-rel>=</mo>
   <mfrac>
      <mrow>
         <mi>c</mi>
      </mrow>
      <mrow>
         <mn>2</mn>
         <mi>L</mi>
      </mrow>
   </mfrac>
</math>. We therefore deduce that the solutions are periodic. This result is easy to understand if we reason on the basis of an impulse wave which is reflected on both sides.
<figure class=figure>
<p><img alt="Figure 2. Wave propagating inside a Medium bounded by two walls." src=${config.ASSETS_URL}/01/08/theory/figure-2.svg><figcaption class=caption>Figure 2. Wave propagating inside a Medium bounded by two walls.</figcaption></figure>
<p>Having reflected on the second wall, the wave returns to reflect on the first wall and rebounds indefinitely (since there are no losses in our model) between the two walls (see <a href="#" data-img-src=${config.ASSETS_URL}/01/08/theory/figure-2.svg data-img-alt="Figure 2. Wave propagating inside a Medium bounded by two walls.">Figure 2</a>).
<h3 class=sectionHead>Solutions in frequency domain</h3>
<p>
The general solution of the wave equation in frequency domain (that is to say for a pure tone of pulsation
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <mi>ω</mi>
</math>) is written for the complex amplitudes of the pressure and the particle velocity
<table class=equation>
<tr>
   <td>
      <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
         <mi>p</mi>
         <mo class=MathClass-open>(</mo>
         <mi>x</mi>
         <mo class=MathClass-punc>,</mo>
         <mi>ω</mi>
         <mo class=MathClass-close>)</mo>
         <mo class=MathClass-rel>=</mo>
         <mi>A</mi>
         <msup>
            <mrow>
               <mi>e</mi>
            </mrow>
            <mrow>
               <mo class=MathClass-bin>−</mo>
               <mi mathvariant=italic>jkx</mi>
            </mrow>
         </msup>
         <mo class=MathClass-bin>+</mo>
         <msup>
            <mrow>
               <mi>B</mi>
            </mrow>
            <mrow>
               <mo class=MathClass-bin>+</mo>
               <mi mathvariant=italic>jkx</mi>
            </mrow>
         </msup>
      </math>
   <td class=eq-no>(5)
<tr>
   <td>
      <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
         <mi>v</mi>
         <mo class=MathClass-open>(</mo>
         <mi>x</mi>
         <mo class=MathClass-punc>,</mo>
         <mi>ω</mi>
         <mo class=MathClass-close>)</mo>
         <mo class=MathClass-rel>=</mo>
         <mfrac>
            <mrow>
               <mi>A</mi>
               <msup>
                  <mrow>
                     <mi>e</mi>
                  </mrow>
                  <mrow>
                     <mo class=MathClass-bin>−</mo>
                     <mi mathvariant=italic>jkx</mi>
                  </mrow>
               </msup>
               <mo class=MathClass-bin>−</mo>
               <msup>
                  <mrow>
                     <mi>B</mi>
                  </mrow>
                  <mrow>
                     <mo class=MathClass-bin>+</mo>
                     <mi mathvariant=italic>jkx</mi>
                  </mrow>
               </msup>
            </mrow>
            <mrow>
               <msub>
                  <mrow>
                     <mi>ρ</mi>
                  </mrow>
                  <mrow>
                     <mn>0</mn>
                  </mrow>
               </msub>
               <mi>c</mi>
            </mrow>
         </mfrac>
      </math>
   <td class=eq-no>(6)
</table>
<p>
At
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <mi>x</mi>
   <mo class=MathClass-rel>=</mo>
   <mn>0</mn>
</math>, for a rigid wall
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <mi>v</mi>
   <mo class=MathClass-open>(</mo>
   <mn>0</mn>
   <mo class=MathClass-punc>,</mo>
   <mi>ω</mi>
   <mo class=MathClass-close>)</mo>
   <mo class=MathClass-rel>=</mo>
   <mn>0</mn>
</math>, then
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <mi>A</mi>
   <mo class=MathClass-rel>=</mo>
   <mi>B</mi>
</math>
which leads to
<table class=equation>
<tr>
   <td>
      <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
         <mi>v</mi>
         <mo class=MathClass-open>(</mo>
         <mi>x</mi>
         <mo class=MathClass-punc>,</mo>
         <mi>ω</mi>
         <mo class=MathClass-close>)</mo>
         <mo class=MathClass-rel>=</mo>
         <mfrac>
            <mrow>
               <mn>2</mn>
               <mi mathvariant=italic>jA</mi>
               <mi class=qopname>sin</mi>
               <mo>⁡</mo>
               <mi mathvariant=italic>kx</mi>
            </mrow>
            <mrow>
               <msub>
                  <mrow>
                     <mi>ρ</mi>
                  </mrow>
                  <mrow>
                     <mn>0</mn>
                  </mrow>
               </msub>
               <mi>c</mi>
            </mrow>
         </mfrac>
      </math>
   <td class=eq-no>(7)
</table>
<p>
At
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <mi>x</mi>
   <mo class=MathClass-rel>=</mo>
   <mi>L</mi>
</math>
as well
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <mi>v</mi>
   <mo class=MathClass-open>(</mo>
   <mi>L</mi>
   <mo class=MathClass-punc>,</mo>
   <mi>ω</mi>
   <mo class=MathClass-close>)</mo>
   <mo class=MathClass-rel>=</mo>
   <mn>0</mn>
</math>, then
<table class=equation>
<tr>
   <td>
      <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
         <mi>A</mi>
         <mi class=qopname>sin</mi>
         <mo>⁡</mo>
         <mo class=MathClass-open>(</mo>
         <mi mathvariant=italic>kL</mi>
         <mo class=MathClass-close>)</mo>
         <mo class=MathClass-rel>=</mo>
         <mn>0</mn>
      </math>
   <td class=eq-no>(8)
</table>
<p>Two situations have to be considered.
<p>
a)
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <mi>A</mi>
   <mo class=MathClass-rel>=</mo>
   <mn>0</mn>
</math>. This is the trivial solution corresponding to absolute silence (no oscillation).
<p>
b)
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <mi class=qopname>sin</mi>
   <mo>⁡</mo>
   <mo class=MathClass-open>(</mo>
   <mi mathvariant=italic>kL</mi>
   <mo class=MathClass-close>)</mo>
   <mo class=MathClass-rel>=</mo>
   <mn>0</mn>
</math>
that is
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <mi mathvariant=italic>kL</mi>
   <mo class=MathClass-rel>=</mo>
   <mi mathvariant=italic>nπ</mi>
</math>
with
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <mi>n</mi>
</math>
any integer.
<p>
In other words, there are non-zero solutions only if the frequency is a multiple of the frequency
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <msub>
      <mrow>
         <mi>f</mi>
      </mrow>
      <mrow>
         <mn>1</mn>
      </mrow>
   </msub>
   <mo class=MathClass-rel>=</mo>
   <mi>c</mi>
   <mo class=MathClass-bin>∕</mo>
   <mn>2</mn>
   <mi>L</mi>
</math>. These frequencies are called natural frequencies of the system and the corresponding solutions are called the eigenmodes (see <a href="#" data-img-src=${config.ASSETS_URL}/01/08/theory/figure-3.svg data-img-alt="Figure 3. First three eigenmodes of a 1D cavity (close-close cylinder). Curves represent the envelope of the volume velocity. Eigenfrequencies are given by <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><msub><mrow><mi>f</mi></mrow><mrow><mi>n</mi></mrow></msub><mo class=MathClass-rel>=</mo><mfrac><mrow><mi mathvariant=italic>nc</mi></mrow><mrow><mn>2</mn><mi>L</mi></mrow></mfrac></math> with <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><mi>c</mi></math> the speed of waves and <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><mi>L</mi></math> the length of the cylinder.">Figure 3</a>).
<figure class=figure>
<p><img alt="Figure 3. First three eigenmodes of a 1D cavity (close-close cylinder). Curves represent the envelope of the volume velocity. Eigenfrequencies are given by <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><msub><mrow><mi>f</mi></mrow><mrow><mi>n</mi></mrow></msub><mo class=MathClass-rel>=</mo><mfrac><mrow><mi mathvariant=italic>nc</mi></mrow><mrow><mn>2</mn><mi>L</mi></mrow></mfrac></math> with <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><mi>c</mi></math> the speed of waves and <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><mi>L</mi></math> the length of the cylinder." src=${config.ASSETS_URL}/01/08/theory/figure-3.svg><figcaption class=caption>Figure 3. First three eigenmodes of a 1D cavity (close-close cylinder). Curves represent the envelope of the volume velocity. Eigenfrequencies are given by <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><msub><mrow><mi>f</mi></mrow><mrow><mi>n</mi></mrow></msub><mo class=MathClass-rel>=</mo><mfrac><mrow><mi mathvariant=italic>nc</mi></mrow><mrow><mn>2</mn><mi>L</mi></mrow></mfrac></math> with <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><mi>c</mi></math> the speed of waves and <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><mi>L</mi></math> the length of the cylinder.</figcaption></figure>
<h3 class=sectionHead>Relationship between the solutions in the time domain and in the frequency domain</h3>
<p>The following results have been obtained
<p>
In the time domain the solution is periodic with the frequency
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <msub>
      <mrow>
         <mi>f</mi>
      </mrow>
      <mrow>
         <mn>1</mn>
      </mrow>
   </msub>
   <mo class=MathClass-rel>=</mo>
   <mi>c</mi>
   <mo class=MathClass-bin>∕</mo>
   <mn>2</mn>
   <mi>L</mi>
</math>.
<p>
In the frequency domain the only nonzero solution appear for frequencies
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <msub>
      <mrow>
         <mi>f</mi>
      </mrow>
      <mrow>
         <mi>n</mi>
      </mrow>
   </msub>
   <mo class=MathClass-rel>=</mo>
   <mi>n</mi>
   <msub>
      <mrow>
         <mi>f</mi>
      </mrow>
      <mrow>
         <mn>1</mn>
      </mrow>
   </msub>
   <mo class=MathClass-rel>=</mo>
   <mi>n</mi>
   <mfrac>
      <mrow>
         <mi>c</mi>
      </mrow>
      <mrow>
         <mn>2</mn>
         <mi>L</mi>
      </mrow>
   </mfrac>
</math>.
<p>These two results are equivalent. Indeed, from the time domain to the frequency domain a Fourier transform has to be done. Now, the Fourier transform of a periodic signal is a Fourier series that is to say that the spectrum contains only multiple frequencies of the fundamental frequency: every time domain solution is thus obtained by the sum of all modes. These are the amplitudes of the modes that set the solutions. Note that in the case discussed here the eigenfrequencies are harmonic which is not the case in general, for example in the case where the impedance of the wall is frequency dependent or in 2D or 3D problems. In such a situation the solution is no more periodic.
<h3 class=sectionHead>Eigenmodes and resonance</h3>
<p>
The concept of eigenmode and the concept of resonance are two concepts often associated which often leads to confusion between the two concepts. Yet, these two concepts are fundamentally different: in fact the concept of proper mode, as the name suggests, is for a passive system. Proper modes are possible ways (solutions) to vibrate for the system (knowing that any linear combination of different modes is also a possible solution) regardless of any source. The concept of resonance exists in the presence of a source. If this source is active, there is a resonance when the amplitude of a physical quantity is maximum. The resonance frequencies, when a source is coupled to a passive system can sometimes be quite close to the natural frequency of the passive system, in particular when the source is weakly coupled to the passive system (that is to say, the source is little affected by the behavior of the passive system). This is why there is sometimes some confusion between the two concepts. To illustrate the relationship between resonances and eigenmodes we consider the case of a medium bounded by two perfectly reflecting walls, one of the wall vibrates with a velocity amplitude
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <mi>v</mi>
   <mo class=MathClass-open>(</mo>
   <mi>x</mi>
   <mo class=MathClass-rel>=</mo>
   <mi>L</mi>
   <mo class=MathClass-punc>,</mo>
   <mi>ω</mi>
   <mo class=MathClass-close>)</mo>
   <mo class=MathClass-rel>=</mo>
   <msub>
      <mrow>
         <mi>v</mi>
      </mrow>
      <mrow>
         <mi>L</mi>
      </mrow>
   </msub>
</math> (see <a href="#" data-img-src=${config.ASSETS_URL}/01/08/theory/figure-4.svg data-img-alt="Figure 4. Medium bounded by a rigid wall on one side and a vibrating wall on the other.">Figure 4</a>). In this case, the movement of the wall is assumed to be fully independent of the load (that is to say, the passive system which is coupled to the vibrating wall) and is therefore a case of weak coupling.
<figure class=figure>
<p><img alt="Figure 4. Medium bounded by a rigid wall on one side and a vibrating wall on the other." src=${config.ASSETS_URL}/01/08/theory/figure-4.svg><figcaption class=caption>Figure 4. Medium bounded by a rigid wall on one side and a vibrating wall on the other.</figcaption></figure>
<p>
As at
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <mi>x</mi>
   <mo class=MathClass-rel>=</mo>
   <mn>0</mn>
</math>
is a perfectly rigid wall, the solution for the velocity is given by (see Equation 7)
<table class=equation>
<tr>
   <td>
      <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
         <mi>v</mi>
         <mo class=MathClass-open>(</mo>
         <mi>x</mi>
         <mo class=MathClass-punc>,</mo>
         <mi>ω</mi>
         <mo class=MathClass-close>)</mo>
         <mo class=MathClass-rel>=</mo>
         <mfrac>
            <mrow>
               <mn>2</mn>
               <mi mathvariant=italic>jA</mi>
               <mi class=qopname>sin</mi>
               <mo>⁡</mo>
               <mi mathvariant=italic>kx</mi>
            </mrow>
            <mrow>
               <msub>
                  <mrow>
                     <mi>ρ</mi>
                  </mrow>
                  <mrow>
                     <mn>0</mn>
                  </mrow>
               </msub>
               <mi>c</mi>
            </mrow>
         </mfrac>
      </math>
   <td class=eq-no>(9)
</table>
<p>
At
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <mi>x</mi>
   <mo class=MathClass-rel>=</mo>
   <mi>L</mi>
</math>
we have
<table class=equation>
<tr>
   <td>
      <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
         <mi>v</mi>
         <mo class=MathClass-open>(</mo>
         <mi>L</mi>
         <mo class=MathClass-punc>,</mo>
         <mi>ω</mi>
         <mo class=MathClass-close>)</mo>
         <mo class=MathClass-rel>=</mo>
         <mfrac>
            <mrow>
               <mn>2</mn>
               <mi mathvariant=italic>jA</mi>
               <mi class=qopname>sin</mi>
               <mo>⁡</mo>
               <mi mathvariant=italic>kL</mi>
            </mrow>
            <mrow>
               <msub>
                  <mrow>
                     <mi>ρ</mi>
                  </mrow>
                  <mrow>
                     <mn>0</mn>
                  </mrow>
               </msub>
               <mi>c</mi>
            </mrow>
         </mfrac>
         <mo class=MathClass-rel>=</mo>
         <msub>
            <mrow>
               <mi>v</mi>
            </mrow>
            <mrow>
               <mi>L</mi>
            </mrow>
         </msub>
      </math>
   <td class=eq-no>(10)
</table>
<p>We deduce
<table class=equation>
<tr>
   <td>
      <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
         <mi>A</mi>
         <mo class=MathClass-rel>=</mo>
         <msub>
            <mrow>
               <mi>ρ</mi>
            </mrow>
            <mrow>
               <mn>0</mn>
            </mrow>
         </msub>
         <mi>c</mi>
         <mfrac>
            <mrow>
               <msub>
                  <mrow>
                     <mi>v</mi>
                  </mrow>
                  <mrow>
                     <mi>L</mi>
                  </mrow>
               </msub>
            </mrow>
            <mrow>
               <mn>2</mn>
               <mi>j</mi>
               <mi class=qopname>sin</mi>
               <mo>⁡</mo>
               <mi mathvariant=italic>kL</mi>
            </mrow>
         </mfrac>
      </math>
   <td class=eq-no>(11)
</table>
<p>then
<table class=equation>
<tr>
   <td>
      <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
         <mi>v</mi>
         <mo class=MathClass-open>(</mo>
         <mi>x</mi>
         <mo class=MathClass-punc>,</mo>
         <mi>ω</mi>
         <mo class=MathClass-close>)</mo>
         <mo class=MathClass-rel>=</mo>
         <msub>
            <mrow>
               <mi>v</mi>
            </mrow>
            <mrow>
               <mi>L</mi>
            </mrow>
         </msub>
         <mfrac>
            <mrow>
               <mi class=qopname>sin</mi>
               <mo>⁡</mo>
               <mi mathvariant=italic>kx</mi>
            </mrow>
            <mrow>
               <mi class=qopname>sin</mi>
               <mo>⁡</mo>
               <mi mathvariant=italic>kL</mi>
            </mrow>
         </mfrac>
      </math>
   <td class=eq-no>(12)
</table>
<p>
For the pressure as well we have
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <mi>p</mi>
   <mo class=MathClass-open>(</mo>
   <mi>x</mi>
   <mo class=MathClass-punc>,</mo>
   <mi>ω</mi>
   <mo class=MathClass-close>)</mo>
   <mo class=MathClass-rel>=</mo>
   <mn>2</mn>
   <mi>A</mi>
   <mi class=qopname>cos</mi>
   <mo>⁡</mo>
   <mi mathvariant=italic>kx</mi>
</math>, then
<table class=equation>
<tr>
   <td>
      <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
         <mi>p</mi>
         <mo class=MathClass-open>(</mo>
         <mi>x</mi>
         <mo class=MathClass-punc>,</mo>
         <mi>ω</mi>
         <mo class=MathClass-close>)</mo>
         <mo class=MathClass-rel>=</mo>
         <msub>
            <mrow>
               <mi>ρ</mi>
            </mrow>
            <mrow>
               <mn>0</mn>
            </mrow>
         </msub>
         <mi mathvariant=italic>c </mi>
         <msub>
            <mrow>
               <mi>v</mi>
            </mrow>
            <mrow>
               <mi>L</mi>
            </mrow>
         </msub>
         <mfrac>
            <mrow>
               <mi class=qopname>cos</mi>
               <mo>⁡</mo>
               <mi mathvariant=italic>kx</mi>
            </mrow>
            <mrow>
               <mi>j</mi>
               <mi class=qopname>sin</mi>
               <mo>⁡</mo>
               <mi mathvariant=italic>kL</mi>
            </mrow>
         </mfrac>
      </math>
   <td class=eq-no>(13)
</table>
<p>
We see that if
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <mi mathvariant=italic>kL</mi>
   <mo class=MathClass-rel>≠</mo>
   <mi mathvariant=italic>nπ</mi>
</math>
we have a finite amplitude while if
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <mi mathvariant=italic>kL</mi>
   <mo class=MathClass-rel>=</mo>
   <mi mathvariant=italic>nπ</mi>
</math>
we have an infinite amplitude: this is the resonance phenomenon. In this example the resonance frequencies coincide with the natural frequencies. Note that in the problem of non-vibrating walls solutions have a finite amplitude if
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <mi mathvariant=italic>kL</mi>
   <mo class=MathClass-rel>=</mo>
   <mi mathvariant=italic>nπ</mi>
</math>
and a zero amplitude if
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <mi mathvariant=italic>kL</mi>
   <mo class=MathClass-rel>≠</mo>
   <mi mathvariant=italic>nπ</mi>
</math>. This difference is explained by the fact that when the wall vibrates, energy is continuously injected into the system. Outside the resonance destructive interference phenomenon limit the amplitude of the oscillations. At resonance the energy accumulates due to constructive interferences and considering that the model does not take into account losses, amplitude tends to infinity. In reality, the amplitude at resonance reaches a finite value, sometimes very high, depending on the losses in the system.
<h3 class=likesectionHead>Glossary</h3>
<dl>
  <dt>2D
  <dd>2-Dimensions
  <dt>3D
  <dd>3-Dimensions
</dl>
<h3 class=sectionHead>References</h3>
<dl class=thebibliography>
<dt>[1]
<dd>Leo J. Beranek. Acoustics. The American Institute of Physics, New York, 1986.
<dt>[2]
<dd>A.D. Pierce. Acoustics: an Introduction to its Physical Principles and Applications. MCGraw-Hill Book Company, Inc., New York, 1981.
<dt>[3]
<dd>S. Temkin. Elements of Acoustics. Wiley, New York, 1981.
</dl>`;

export default theory;
